import type { FluxErrorEntityErrorCodes } from '../types.js';

export const CastValueErrorCodes = {
  CastValueInvalidBoolean: {
    code: 'cast-value/invalid-boolean',
    explanation: `Esse erro ocorre quando o sistema tenta **converter um valor para booleano** mas o valor fornecido não é reconhecido como verdadeiro ou falso.

**Valores aceitos:**
- Booleanos: \`true\`, \`false\`
- Strings: \`"true"\`, \`"false"\`, \`"1"\`, \`"0"\`, \`"yes"\`, \`"no"\`
- Números: \`1\` (true), \`0\` (false)

**Como resolver:**
Verifique se o valor enviado está em um dos formatos aceitos. Valores como \`"sim"\`, \`"não"\`, \`2\`, ou strings vazias não são válidos.`,
    localizations: {},
    message: 'O valor fornecido não é um booleano válido.',
  },
  CastValueInvalidDate: {
    code: 'cast-value/invalid-date',
    explanation: `Esse erro ocorre quando o sistema tenta **converter um valor para data** mas o formato fornecido não é reconhecido como uma data válida.

**Formatos aceitos:**
- ISO 8601: \`"2024-12-16"\`, \`"2024-12-16T14:30:00Z"\`
- Timestamp Unix: \`1702742400000\`

**Causas comuns:**
- Data em formato incorreto (ex: \`"16/12/2024"\` quando se espera \`"2024-12-16"\`)
- Valores de data inválidos (ex: \`"2024-02-30"\`, \`"2024-13-01"\`)
- Strings que não representam datas (ex: \`"invalid"\`, \`"abc"\`)

**Como resolver:**
Verifique se a data está no formato ISO 8601 ou em um formato aceito pelo sistema. Use bibliotecas de data para garantir formatação correta.`,
    localizations: {},
    message: 'O valor fornecido não é uma data válida.',
  },
  CastValueInvalidJson: {
    code: 'cast-value/invalid-json',
    explanation: `Esse erro ocorre quando o sistema tenta **converter um valor para JSON** mas o valor fornecido não é um objeto válido ou uma string JSON bem-formatada.

**Valores aceitos:**
- Objetos JSON: \`{ "key": "value" }\`
- Arrays JSON: \`[1, 2, 3]\`
- Strings JSON válidas: \`'{"a": 1}'\`

**Causas comuns:**
- String JSON malformada (ex: faltando aspas ou chaves)
- Tentar converter valores que não são objetos nem strings JSON (ex: funções, instâncias de classes não serializáveis)

**Como resolver:**
Garanta que o valor enviado é um objeto JavaScript puro ou uma string que pode ser processada por \`JSON.parse()\`.`,
    localizations: {},
    message: 'O valor fornecido não é um JSON válido.',
  },
  CastValueInvalidNull: {
    code: 'cast-value/invalid-null',
    explanation: `Esse erro ocorre quando o sistema espera um **valor nulo** (\`null\`) mas recebe um valor que não pode ser interpretado como nulo.

**Valores aceitos como nulo:**
- \`null\`
- \`undefined\` (em alguns contextos)
- String vazia \`""\` (dependendo da configuração)

**Causas comuns:**
- Envio de strings como \`"null"\` ou \`"undefined"\` ao invés dos valores literais
- Campos que deveriam estar ausentes mas foram enviados com valores vazios

**Como resolver:**
Se o campo é opcional, não envie o campo ou envie \`null\` literal. Evite enviar strings representando nulo.`,
    localizations: {},
    message: 'O valor fornecido não é nulo válido.',
  },
  CastValueInvalidNumber: {
    code: 'cast-value/invalid-number',
    explanation: `Esse erro ocorre quando o sistema tenta **converter um valor para número** mas o valor fornecido não é reconhecido como numérico.

**Valores aceitos:**
- Números inteiros: \`42\`, \`-10\`, \`0\`
- Números decimais: \`3.14\`, \`-2.5\`, \`0.001\`
- Notação científica: \`1e10\`, \`2.5e-3\`
- Strings numéricas: \`"42"\`, \`"3.14"\`

**Causas comuns:**
- Strings não numéricas (ex: \`"abc"\`, \`"R$ 100"\`, \`"10,50"\` com vírgula)
- Valores especiais como \`NaN\`, \`Infinity\` em alguns contextos
- Números formatados com separadores de milhar (ex: \`"1.000.000"\`)

**Como resolver:**
Remova formatação de moeda, use ponto como separador decimal, e garanta que o valor contém apenas dígitos e opcionalmente um ponto decimal.`,
    localizations: {},
    message: 'O valor fornecido não é um número válido.',
  },
  CastValueInvalidString: {
    code: 'cast-value/invalid-string',
    explanation: `Esse erro ocorre quando o sistema tenta **converter um valor para string** mas o valor fornecido não pode ser representado como texto.

**Causas comuns:**
- Objetos complexos sendo passados ao invés de sua propriedade ou uma versão em JSON
- Valores \`undefined\` em contextos onde não são permitidos

**Como resolver:**
Garanta que o valor pode ser representado como string. Para objetos, use \`toJson\` ou implemente um método \`toString()\` apropriado. Para valores primitivos, a conversão geralmente é automática.`,
    localizations: {},
    message: 'O valor fornecido não é uma string válida.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'cast-value'>;

export const ValidationErrorCodes = {
  ValidationEmailAlreadyInvited: {
    code: 'validation/email-already-invited',
    explanation: `Esse erro ocorre quando você tenta **enviar um convite para um e-mail que já possui um convite pendente** no sistema.

**Regra de negócio:**
Apenas **um convite ativo por e-mail** é permitido por vez. Isso evita spam e garante que o usuário não receba múltiplos convites simultâneos.

**Cenários comuns:**
- O e-mail já foi convidado e o convite ainda está **pendente de aceitação**
- O convite anterior não foi **aceito nem recusado**

**Como resolver:**
Convites **recusados ou expirados** podem ser reenviados. Verifique o status do convite existente antes de tentar enviar um novo. Se necessário, cancele o convite anterior e envie um novo.`,
    localizations: {},
    message: 'Este e-mail já foi convidado para acessar a plataforma.',
  },
  ValidationEmailAlreadyRegistered: {
    code: 'validation/email-already-registered',
    explanation: `Esse erro ocorre quando você tenta **registrar um e-mail que já possui uma conta ativa** no sistema.

**Regra de negócio:**
Cada e-mail pode ser usado para **apenas uma conta** no sistema. Isso garante unicidade e segurança das contas.

**Cenários comuns:**
- O usuário já possui uma conta e esqueceu
- Tentativa de criar uma conta duplicada
- O e-mail foi registrado anteriormente mas a conta pode estar **inativa**

**Como resolver:**
Oriente o usuário a fazer **login** ao invés de criar uma nova conta. Se ele esqueceu a senha, use o fluxo de **recuperação de senha**. Se a conta estiver inativa, pode ser necessário reativá-la.`,
    localizations: {},
    message: 'Este e-mail já está cadastrado. Tente fazer login.',
  },
  ValidationEmailDomainNotAllowed: {
    code: 'validation/email-domain-not-allowed',
    explanation: `Esse erro ocorre quando o **domínio do e-mail está na lista de bloqueio** do sistema.

**Motivos para bloqueio:**
- **Domínios descartáveis** (temporary email services)
- **Domínios conhecidos por spam** ou atividades maliciosas

**Exemplos de domínios bloqueados:**
- E-mails temporários: \`tempmail.com\`, \`10minutemail.com\`
- Domínios genéricos bloqueados por política

**Como resolver:**
Oriente o usuário a usar um e-mail de um domínio permitido e conhecido.`,
    localizations: {},
    message: 'O domínio de e-mail informado não é permitido.',
  },
  ValidationEmailDomainNotFound: {
    code: 'validation/email-domain-not-found',
    explanation: `Esse erro ocorre quando o **domínio do e-mail não existe** ou não pode ser verificado via DNS.

**Causas comuns:**
- **Erro de digitação** no domínio (ex: \`gmial.com\` ao invés de \`gmail.com\`)
- Domínio **não registrado** ou **expirado**
- Problemas de **DNS** que impedem a resolução do domínio
- Domínio **inválido** ou malformado

**Validação realizada:**
O sistema verifica se o domínio possui registros **MX (Mail Exchange)** válidos no DNS, que são necessários para receber e-mails.

**Como resolver:**
Use ferramentas de DNS lookup para confirmar que o domínio existe e possui registros MX configurados.`,
    localizations: {},
    message: 'Domínio de e-mail não encontrado.',
  },
  ValidationEmailFluxDomainNotAllowed: {
    code: 'validation/email-flux-domain-not-allowed',
    explanation: `Esse erro ocorre quando o **domínio do e-mail é do flux**.

**Motivos para bloqueio:**
- **Domínios como @fluxcontrol.com.br**
- **Domínios como @teams.fluxcontrol.com.br**

**Exemplos de domínios bloqueados:**
- \`joao@fluxcontrol.com.br\`
- \`time-adbce@teams.fluxcontrol.com.br\`

**Como resolver:**
Use um e-mail de um domínio permitido e conhecido.`,
    localizations: {},
    message: 'O domínio do flux não pode ser usado.',
  },
  ValidationInvalidEmailFormat: {
    code: 'validation/invalid-email-format',
    explanation: `Esse erro ocorre quando o **formato do e-mail não segue o padrão RFC 5322** para endereços de e-mail.

**Formato esperado:**
\`usuario@dominio.extensao\`

**Exemplos válidos:**
- \`joao.silva@empresa.com.br\`
- \`maria+tag@exemplo.com\`
- \`user_123@sub.dominio.org\`

**Erros comuns:**
- Falta do \`@\`: \`usuariodominio.com\`
- Múltiplos \`@\`: \`usuario@@dominio.com\`
- Espaços: \`usuario @dominio.com\`
- Caracteres inválidos: \`usuário@domínio.com\` (acentos não são permitidos)
- Domínio sem extensão: \`usuario@dominio\`

**Como resolver:**
Verifique se o e-mail segue o formato padrão.`,
    localizations: {},
    message: 'O formato do e-mail informado é inválido.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'validation'>;

export const WorkflowErrorCodes = {
  WorkflowCardCannotSkipPendingTasks: {
    code: 'workflow/card-cannot-skip-pending-tasks',
    explanation:
      'O card não pode ser movido para esta etapa porque há tarefas pendentes em etapas intermediárias. Esse comportamento é configurável nas definições da etapa.',
    localizations: {},
    message:
      'O processo não pode ser movido para esta etapa porque ainda há tarefas pendentes entre as etapas.',
    supportingDocs: [
      {
        name: 'Configurações da Etapa',
        path: '/wiki/docs/pipe/stages/configuration',
      },
      {
        name: 'Tarefas do Processo',
        path: '/wiki/docs/cards/tasks/intro',
      },
    ],
  },
  WorkflowCardHasTaskNotDoneEnter: {
    code: 'workflow/card-has-task-not-done-enter',
    explanation:
      'O card não pode ser movido para esta etapa porque há tarefas pendentes que precisam ser concluídas primeiro. Esse comportamento é configurável nas definições da etapa.',
    localizations: {},
    message:
      'Não é possível mover o processo para esta etapa porque ainda há tarefas pendentes a serem concluídas.',
    supportingDocs: [
      {
        name: 'Configurações da Etapa',
        path: '/wiki/docs/pipe/stages/configuration#cards-com-tarefas-não-finalizadas-podem-ser-movidos-para-esta-etapa',
      },
      {
        name: 'Tarefas do Processo',
        path: '/wiki/docs/cards/tasks/intro',
      },
    ],
  },
  WorkflowCardHasTaskNotDoneLeave: {
    code: 'workflow/card-has-task-not-done-leave',
    explanation:
      'O card não pode ser movido desta etapa porque há tarefas pendentes que precisam ser concluídas primeiro. Esse comportamento é configurável nas definições da etapa.',
    localizations: {},
    message:
      'Não é possível mover o processo desta etapa porque ainda há tarefas pendentes. Complete todas as tarefas antes de prosseguir.',
    supportingDocs: [
      {
        name: 'Configurações da Etapa',
        path: '/wiki/docs/pipe/stages/configuration#cards-com-tarefas-não-finalizadas-podem-ser-movidos-desta-etapa',
      },
      {
        name: 'Tarefas do Processo',
        path: '/wiki/docs/cards/tasks/intro',
      },
    ],
  },
  WorkflowCardNotApproved: {
    code: 'workflow/card-not-approved',
    explanation:
      'O card não pode ser movido para a próxima etapa porque precisa ser aprovado primeiro. Esse comportamento é configurável nas definições da etapa.',
    localizations: {},
    message: 'Este processo precisa de aprovação antes de ser movido para a próxima etapa.',
    supportingDocs: [
      {
        name: 'Configurações da Etapa',
        path: '/wiki/docs/pipe/stages/configuration',
      },
      {
        name: 'Revisando um processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  WorkflowCardNotHasPermissionToMove: {
    code: 'workflow/card-not-has-permission-to-move',
    explanation:
      'Você não tem permissão para mover este card. Etapas podem ser configuradas para apenas permitir que processos sejam movidos para certas etapas.',
    localizations: {},
    message:
      'Você não tem permissão para mover este processo para esta etapa devido a uma configuração da etapa.',
    supportingDocs: [
      {
        name: 'Configurações da Etapa',
        path: '/wiki/docs/pipe/stages/configuration#ação',
      },
    ],
  },
  WorkflowCustomValidationField: {
    code: 'workflow/custom-validation-failed',
    explanation:
      'A validação personalizada de um campo no formulário falhou. Verifique se todos os campos com validação personalizada foram preenchidos corretamente.',
    localizations: {},
    message: 'Existe um campo com validação personalizada que não foi preenchido corretamente.',
    supportingDocs: [
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms#validações-personalizadas',
      },
    ],
  },
  WorkflowFieldsRequiredNotAnswered: {
    code: 'workflow/fields-required-not-answered',
    explanation:
      'Um ou mais campos obrigatórios no workflow não foram preenchidos. Verifique tanto o formulário inicial quanto os formulários específicos da etapa atual e complete todos os campos marcados com asterisco (*).',
    localizations: {},
    message: 'Existem campos obrigatórios não preenchidos no fluxo de trabalho.',
    supportingDocs: [
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
      {
        name: 'Aba Geral do Processo',
        path: '/wiki/docs/cards/tabs/general#formulários',
      },
    ],
  },
  WorkflowPipesLabelNameRequired: {
    code: 'workflow/pipes-label-name-required',
    explanation: 'O nome da etiqueta é obrigatório para esta operação.',
    localizations: {},
    message: 'O nome da etiqueta é obrigatório para prosseguir.',
  },
  WorkflowPipesLabelNotFound: {
    code: 'workflow/pipes-label-not-found',
    explanation: 'A etiqueta especificada não foi encontrada neste workflow.',
    localizations: {},
    message: 'A etiqueta especificada não foi encontrada.',
  },
  WorkflowPipesLabelSameName: {
    code: 'workflow/pipes-label-same-name',
    explanation: 'Já existe uma etiqueta com o mesmo nome neste workflow.',
    localizations: {},
    message: 'Já existe uma etiqueta com este mesmo nome.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'workflow'>;
